import React, { useEffect, useState, useCallback, useRef, useId } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from "react-redux";
import { Result } from "../../models/general";
import { setCurrentStatusText } from "../../store/redux/actions/userActionCreator";
import { UpdateCustomStatus, GetStatusHistory } from "../../api/General/account";
import { StatusHistory } from "../../models/attendance";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/Dialog";
import Button from "../ui/Button";
import AppIcon from "../AppIcon";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import type { RootState } from "../../store/redux/store/store";

interface IProps {
    semibold: boolean;
    asDialog: boolean;
    dialogIsOpen: boolean;
    closeAvailabilityStatusDialog: () => void;
}

const MAX_LEN = 120;

/* ─── Shared sub-component for status input with history ─── */
function StatusInput({
  value,
  onChange,
  onSubmit,
  history,
  compact,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  history: string[];
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const filtered = value
    ? history.filter((h) => h.toLowerCase().includes(value.toLowerCase()))
    : history;

  // Close history when clicking outside
  useEffect(() => {
    if (!historyOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!inputRef.current?.contains(target) && !listRef.current?.contains(target)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [historyOpen]);

  const selectItem = (text: string) => {
    onChange(text.slice(0, MAX_LEN));
    setHistoryOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (historyOpen && activeIndex >= 0 && activeIndex < filtered.length) {
        e.preventDefault();
        selectItem(filtered[activeIndex]);
      } else {
        e.preventDefault();
        onSubmit();
      }
    } else if (e.key === 'ArrowDown' && historyOpen) {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp' && historyOpen) {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape' && historyOpen) {
      e.preventDefault();
      setHistoryOpen(false);
      setActiveIndex(-1);
    }
  };

  // Scroll active into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const activeDescendant = activeIndex >= 0 ? `status-option-${activeIndex}` : undefined;

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={historyOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          aria-label={t('Availability Status') || 'Availability Status'}
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={handleKeyDown}
          placeholder={t('Set your availability status') || ''}
          maxLength={MAX_LEN}
          className={cn(
            'flex-1 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary',
            compact ? 'h-8 py-1' : 'h-10 py-2'
          )}
        />
        {history.length > 0 && (
          <button
            type="button"
            onClick={() => { setHistoryOpen((o) => !o); setActiveIndex(-1); }}
            className="rounded p-1 hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            title={t('Show history') || ''}
            aria-label={t('Show status history') || ''}
          >
            <AppIcon name="History" size={16} />
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{value.length}/{MAX_LEN}</p>

      {historyOpen && filtered.length > 0 && (
        <div
          ref={listRef}
          role="listbox"
          id={listboxId}
          className="absolute z-10 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-40 overflow-y-auto"
        >
          {filtered.map((h, i) => (
            <button
              key={i}
              id={`status-option-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              data-index={i}
              className={cn(
                'w-full px-3 py-2 text-start text-sm transition-colors',
                i === activeIndex ? 'bg-muted' : 'hover:bg-muted'
              )}
              onClick={() => selectItem(h)}
            >
              {h}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CurrentStatus = ({ semibold, asDialog, dialogIsOpen, closeAvailabilityStatusDialog }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentCustomStatusText = useSelector((state: RootState) => state.user.currentCustomStatusText);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  const [statusInputText, setStatusInputText] = useState(currentCustomStatusText || "");
  const [history, setHistory] = useState<StatusHistory[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Sync when redux value changes externally
  useEffect(() => {
    setStatusInputText(currentCustomStatusText || "");
  }, [currentCustomStatusText]);

  const fetchHistory = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const result = await GetStatusHistory();
      if (result.errors?.length) {
        setErrorMsg(result.errors[0].description);
        return;
      }
      setHistory(result.payload || []);
    } catch (error: any) {
      console.log(error);
      if (error?.errors?.[0]?.description) {
        setErrorMsg(error.errors[0].description);
      }
    }
  }, [isLoggedIn]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    if (dialogIsOpen) {
      setOpen(true);
      fetchHistory();
    }
  }, [dialogIsOpen, fetchHistory]);

  const closeDialog = () => {
    setOpen(false);
    closeAvailabilityStatusDialog();
  };

  const handleSubmit = async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      await UpdateCustomStatus({ currentCustomStatus: statusInputText.trim() });
      dispatch(setCurrentStatusText(statusInputText.trim()));
      if (asDialog) closeDialog();
    } catch (error: any) {
      console.log("error: ", error);
      if (error?.errors?.[0]) {
        const res = error as Result<never>;
        setErrorMsg(res.errors[0].description);
      } else {
        setErrorMsg(t('An unexpected error occurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      setStatusInputText("");
      await UpdateCustomStatus({ currentCustomStatus: "" });
      dispatch(setCurrentStatusText(""));
      if (asDialog) closeDialog();
    } catch (error: any) {
      console.log("error: ", error);
      if (error?.errors?.[0]) {
        const res = error as Result<never>;
        setErrorMsg(res.errors[0].description);
      } else {
        setErrorMsg(t('An unexpected error occurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  const historyTexts = history?.map(h => h.status).filter(Boolean) || [];

  if (asDialog) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) closeDialog(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AppIcon name="History" size={16} />
              <DialogTitle>{t('Availability Status')}</DialogTitle>
            </div>
          </DialogHeader>
          <div className="p-6 space-y-3">
            {errorMsg && (
              <div
                className="flex items-center justify-between rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
                role="alert"
              >
                <span>{errorMsg}</span>
                <button onClick={() => setErrorMsg(null)} className="ms-2 rounded p-0.5 hover:bg-destructive/20" aria-label={t('Dismiss')}>
                  <AppIcon name="X" size={14} />
                </button>
              </div>
            )}
            <StatusInput
              value={statusInputText}
              onChange={setStatusInputText}
              onSubmit={handleSubmit}
              history={historyTexts}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>{t('Close')}</Button>
            <Button variant="default" onClick={handleSubmit} disabled={loading || !isLoggedIn}>
              {loading ? <Spinner size="sm" className="me-2" /> : <AppIcon name="Check" size={16} className="me-2" />}
              {loading ? t('Saving...') : t('Save')}
            </Button>
            <Button variant="warning" onClick={clear} disabled={loading || !isLoggedIn || !statusInputText}>
              <AppIcon name="X" size={16} className="me-2" />
              {t('Clear')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <p
        className={cn("mb-1 text-sm", semibold && "font-semibold")}
        style={{ color: 'var(--mowazaf-text-dark, #243A78)' }}
      >
        {t('Availability Status')}
      </p>
      {errorMsg && (
        <div
          className="mb-2 flex items-center justify-between rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
          role="alert"
        >
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)} className="ms-2 rounded p-0.5 hover:bg-destructive/20" aria-label={t('Dismiss')}>
            <AppIcon name="X" size={14} />
          </button>
        </div>
      )}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <StatusInput
            value={statusInputText}
            onChange={setStatusInputText}
            onSubmit={handleSubmit}
            history={historyTexts}
            compact
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            className="rounded p-1 hover:bg-muted transition-colors text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={handleSubmit}
            disabled={loading || !isLoggedIn}
            title={t('Save') || ''}
            aria-label={t('Save status') || 'Save status'}
          >
            {loading ? <Spinner size="sm" /> : <AppIcon name="Check" size={20} />}
          </button>
          <button
            className="rounded p-1 hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
            onClick={clear}
            disabled={loading || !isLoggedIn || !statusInputText}
            title={t('Clear') || ''}
            aria-label={t('Clear status') || 'Clear status'}
          >
            <AppIcon name="X" size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CurrentStatus;
